import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Container, Typography, Box, TextField,
  FormControl, InputLabel, Select, MenuItem,
  InputAdornment, alpha, Divider,
} from '@mui/material';
import {
  AccountBalance as BudgetIcon,
  AttachMoney as MoneyIcon,
  Save as SaveIcon,
  CalendarToday as CalendarIcon,
  DriveFileRenameOutline as NameIcon,
  Notes as NotesIcon,
  Straighten as UnitIcon,
  Numbers as NumbersIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axios';
import { useAuth } from '../../../contexts/AuthContext';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmUpdateDialog from '../../common/ConfirmDeleteDialog';
import {
  applyApiValidationErrors,
  formatFieldErrorsMessage,
  getFirstErrorMessage,
  rejectConfirmValidation,
} from '../../../utils/formValidationFeedback';
import CommonSectionCard from '../../common/SectionCard';
import SubmitActionsCard from '../../common/SubmitActionsCard';
import FormDraftNotice from '../../common/FormDraftNotice';
import { SUCCESS } from '../../common/SaaSTokens';
import { canEditBudget } from '../../../utils/financialPermissions';
import useFormDraft from '../../../hooks/useFormDraft';

// ─── Design tokens ────────────────────────────────────────────────────────────
const NEON         = '#38BDF8';
const NEON2        = '#818CF8';
const NEON3        = '#34D399';
const SURFACE_UP   = '#0f1f38';
const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const TEXT_BODY    = '#E2E8F0';
const TEXT_MUTED   = '#94A3B8';
const B            = { 700: '#1D4ED8' };

const FORM_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes form-glow-in { from { box-shadow: 0 0 0 0 rgba(56,189,248,0.45); } to { box-shadow: 0 0 0 4px rgba(56,189,248,0); } }
  .MuiOutlinedInput-input, .MuiInputBase-input {
    color: #E2E8F0 !important;
    -webkit-text-fill-color: #E2E8F0 !important;
    caret-color: #38BDF8 !important;
  }
  .MuiOutlinedInput-input:-webkit-autofill, .MuiInputBase-input:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 100px #0f1f38 inset !important;
    -webkit-text-fill-color: #E2E8F0 !important;
  }
`;

const inputRootSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px', fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem',
    color: alpha(TEXT_BODY, 0.85), background: alpha(SURFACE_UP, 0.7), backdropFilter: 'blur(8px)',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '& fieldset': { borderColor: alpha(NEON, 0.18), transition: 'border-color 0.2s ease' },
    '&:hover fieldset': { borderColor: alpha(NEON, 0.4) },
    '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(NEON, 0.12)}, 0 0 14px ${alpha(NEON, 0.1)}`, animation: 'form-glow-in 0.3s ease' },
    '&.Mui-focused fieldset': { borderColor: alpha(NEON, 0.65), borderWidth: '1.5px' },
    '&.Mui-disabled': { background: alpha(SURFACE_UP, 0.35), '& fieldset': { borderColor: alpha(NEON, 0.07) } },
  },
  '& .MuiInputBase-input': { color: `${alpha(TEXT_BODY, 0.85)} !important`, WebkitTextFillColor: `${alpha(TEXT_BODY, 0.85)} !important`, '&::placeholder': { color: alpha(NEON, 0.3), opacity: 1 } },
  '& .MuiInputLabel-root': { fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: alpha(NEON, 0.45) },
  '& label.Mui-focused': { color: alpha(NEON, 0.85), fontWeight: 600 },
  '& .MuiFormLabel-asterisk': { color: '#EF4444', fontWeight: 700 },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: alpha(NEON, 0.45), fontSize: 18 },
  '& .MuiSelect-icon': { color: alpha(NEON, 0.45) },
  '& .MuiFormHelperText-root': { fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', letterSpacing: 0.3, color: alpha(TEXT_BODY, 0.55) },
  '& .MuiFormHelperText-root.Mui-error': { color: '#F87171' },
  '& .MuiOutlinedInput-root .MuiSelect-select': { color: `${alpha(TEXT_BODY, 0.85)} !important`, WebkitTextFillColor: `${alpha(TEXT_BODY, 0.85)} !important` },
};

const menuPaperSx = {
  background: `linear-gradient(160deg, ${alpha(SURFACE_BASE, 0.98)} 0%, ${alpha(SURFACE_MID, 0.96)} 100%)`,
  border: `1px solid ${alpha(NEON, 0.2)}`,
  boxShadow: `0 8px 32px ${alpha('#000', 0.6)}, inset 0 1px 0 ${alpha(NEON, 0.1)}`,
  backdropFilter: 'blur(12px)',
  '& .MuiMenuItem-root': {
    fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', color: alpha(TEXT_BODY, 0.85), transition: 'all 0.15s ease',
    '&:hover': { background: `linear-gradient(135deg, ${alpha(NEON, 0.12)}, ${alpha(B[700], 0.08)})`, color: NEON },
    '&.Mui-selected': { background: `linear-gradient(135deg, ${alpha(NEON, 0.18)}, ${alpha(B[700], 0.12)})`, color: NEON, fontWeight: 600, '&:hover': { background: `linear-gradient(135deg, ${alpha(NEON, 0.22)}, ${alpha(B[700], 0.15)})` } },
  },
};

const selectControlSx = {
  borderRadius: '10px', fontFamily: "'DM Sans', sans-serif",
  color: alpha(TEXT_BODY, 0.85), background: alpha(SURFACE_UP, 0.7), backdropFilter: 'blur(8px)',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(NEON, 0.18) },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(NEON, 0.4) },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: alpha(NEON, 0.65), borderWidth: '1.5px' },
  '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(NEON, 0.12)}, 0 0 14px ${alpha(NEON, 0.1)}` },
  '& .MuiSelect-select': { color: `${alpha(TEXT_BODY, 0.85)} !important`, WebkitTextFillColor: `${alpha(TEXT_BODY, 0.85)} !important` },
  '& .MuiSelect-icon': { color: alpha(NEON, 0.45) },
};

const MONTHS = [
  { value: 1, label: 'January' },  { value: 2, label: 'February' },
  { value: 3, label: 'March' },    { value: 4, label: 'April' },
  { value: 5, label: 'May' },      { value: 6, label: 'June' },
  { value: 7, label: 'July' },     { value: 8, label: 'August' },
  { value: 9, label: 'September' },{ value: 10, label: 'October' },
  { value: 11, label: 'November' },{ value: 12, label: 'December' },
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR + i);

const DEFAULT_FORM = {
  item: '', price: '', unit: '', amount: '', remark: '',
  year: CURRENT_YEAR,
  month: new Date().getMonth() + 1,
};

const BUDGET_VALIDATION_FIELDS = new Set(['item', 'price', 'unit', 'amount', 'remark', 'year', 'month']);

const SectionCard = ({ title, icon, delay = 0, children }) => (
  <CommonSectionCard title={title} icon={icon} delay={delay}>{children}</CommonSectionCard>
);

const StyledField = ({ sx, ...props }) => (
  <TextField {...props} sx={{ ...inputRootSx, ...sx }} />
);

const StyledSelect = ({ label, value, onChange, children, required, startIcon, sx, error, helperText }) => (
  <FormControl fullWidth required={required} error={error} sx={{ flex: 1, ...inputRootSx, ...sx }}>
    <InputLabel sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: alpha(NEON, 0.45), '&.Mui-focused': { color: alpha(NEON, 0.85), fontWeight: 600 } }}>
      {label}
    </InputLabel>
    <Select value={value} onChange={onChange} label={label}
      startAdornment={startIcon ? <InputAdornment position="start">{startIcon}</InputAdornment> : undefined}
      MenuProps={{ PaperProps: { sx: menuPaperSx } }} sx={selectControlSx}>
      {children}
    </Select>
    {helperText && (
      <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', letterSpacing: 0.3, color: error ? '#F87171' : alpha(TEXT_BODY, 0.55), mt: 0.5, mx: 1.75 }}>
        {helperText}
      </Typography>
    )}
  </FormControl>
);

// ─── Inline calc hint ─────────────────────────────────────────────────────────
const CalcHint = ({ price, unit, amount }) => {
  const p = parseFloat(price);
  const u = parseFloat(unit);
  if (!Number.isFinite(p) || !Number.isFinite(u) || p <= 0 || u <= 0) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderRadius: '8px', background: alpha(NEON3, 0.07), border: `1px solid ${alpha(NEON3, 0.2)}`, width: 'fit-content' }}>
      <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.72rem', color: alpha(NEON3, 0.85), letterSpacing: 0.3 }}>
        RM {p.toLocaleString()} × {u} = <Box component="span" sx={{ color: NEON3, fontWeight: 700 }}>RM {parseFloat(amount).toLocaleString()}</Box>
      </Typography>
    </Box>
  );
};

const adornment = (Icon) => (
  <InputAdornment position="start"><Icon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment>
);

const computeAmountFromPriceUnit = (price, unit) => {
  const p = parseFloat(price), u = parseFloat(unit);
  if (!Number.isFinite(p) || !Number.isFinite(u) || p <= 0 || u <= 0) return null;
  return String(Math.round(p * u * 100) / 100);
};

const buildFormDataFromRecord = (d) => ({
  item: d.item || '', price: d.price || '', unit: d.unit || '',
  amount: d.amount || '', remark: d.remark || '',
  year: d.year || CURRENT_YEAR, month: d.month || new Date().getMonth() + 1,
});

// ─── BudgetForm ───────────────────────────────────────────────────────────────
const BudgetForm = () => {
  const navigate  = useNavigate();
  const { id }    = useParams();
  const location  = useLocation();
  const { user }  = useAuth();

  const [loading,              setLoading]              = useState(false);
  const [dataLoading,          setDataLoading]          = useState(true);
  const [error,                setError]                = useState(null);
  const [successMessage,       setSuccessMessage]       = useState('');
  const [budget,               setBudget]               = useState(location.state?.budget ?? null);
  const [validationErrors,     setValidationErrors]     = useState({});
  const [showFieldErrors,      setShowFieldErrors]      = useState(false);
  const [confirmDialogOpen,    setConfirmDialogOpen]    = useState(false);
  const [pendingUpdateRequest, setPendingUpdateRequest] = useState(null);
  const [confirmDialogError,   setConfirmDialogError]   = useState('');
  const [formData,             setFormData]             = useState(DEFAULT_FORM);

  const isEditMode = Boolean(budget || id);

  useEffect(() => {
    const CSS_ID = 'budgetform-ds-css';
    if (!document.getElementById(CSS_ID)) {
      const el = Object.assign(document.createElement('style'), { id: CSS_ID, innerHTML: FORM_CSS });
      document.head.appendChild(el);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setDataLoading(true);
        if (id && !budget) {
          const { data } = await axiosInstance.get(`/budgets/${id}`);
          const record = data?.data ?? data;
          setBudget(record);
          setFormData(buildFormDataFromRecord(record));
        }
        setError(null);
      } catch { setError('Failed to fetch budget data'); }
      finally { setDataLoading(false); }
    };
    fetchData();
  }, [id, budget]);

  useEffect(() => {
    if (budget) setFormData(buildFormDataFromRecord(budget));
    else if (!id) setFormData(DEFAULT_FORM);
  }, [budget, id]);

  const applyDraft = useCallback((data) => {
    setFormData((prev) => ({ ...DEFAULT_FORM, ...prev, ...data }));
  }, []);
  const getDraftSnapshot = useCallback(() => formData, [formData]);
  const formDraft = useFormDraft({
    formKey: 'budget',
    userId: user?.id,
    isEditMode,
    enabled: !dataLoading,
    getSnapshot: getDraftSnapshot,
    applySnapshot: applyDraft,
    resetForm: () => setFormData(DEFAULT_FORM),
  });

  const handleChange = (field, value) => {
    const nextPrice = field === 'price' ? value : formData.price;
    const nextUnit  = field === 'unit'  ? value : formData.unit;
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'price' || field === 'unit') {
        const computed = computeAmountFromPriceUnit(nextPrice, nextUnit);
        if (computed != null) next.amount = computed;
      }
      return next;
    });
    if (error) setError(null);
    if (showFieldErrors && validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: '' }));
    }
    if (showFieldErrors && (field === 'price' || field === 'unit') && validationErrors.amount) {
      setValidationErrors((prev) => ({ ...prev, amount: '' }));
    }
  };

  const handleNativeChange = (e) => handleChange(e.target.name, e.target.value);

  const collectFormErrors = () => {
    const errors = {};
    if (!formData.item?.trim()) errors.item = 'Item is required';
    if (!formData.amount) errors.amount = 'Amount is required';
    else if (isNaN(parseFloat(formData.amount)) || parseFloat(formData.amount) < 0) {
      errors.amount = 'Must be zero or a positive number';
    }
    if (formData.price !== '' && (isNaN(parseFloat(formData.price)) || parseFloat(formData.price) <= 0)) {
      errors.price = 'Must be a positive number';
    }
    if (formData.unit !== '' && (isNaN(parseFloat(formData.unit)) || parseFloat(formData.unit) <= 0)) {
      errors.unit = 'Quantity must be greater than 0';
    }
    if (!formData.year) errors.year = 'Year is required';
    if (!formData.month) errors.month = 'Month is required';
    return errors;
  };

  const buildPayload = () => ({
    item: formData.item,
    price: formData.price === '' ? null : parseFloat(formData.price),
    unit: formData.unit.trim() || null,
    amount: parseFloat(formData.amount),
    remark: formData.remark.trim() || null,
    year: parseInt(formData.year, 10),
    month: parseInt(formData.month, 10),
  });

  const validateForm = () => {
    const errors = collectFormErrors();
    setValidationErrors(errors);
    setShowFieldErrors(true);
    if (Object.keys(errors).length > 0) {
      setError(formatFieldErrorsMessage(errors));
      return false;
    }
    setError(null);
    return true;
  };

  const handleApiError = (err, fallbackMessage) => {
    const data = err?.response?.data;
    applyApiValidationErrors(data, setValidationErrors, BUDGET_VALIDATION_FIELDS);
    const fieldMsgs = {};
    const errs = data?.errors;
    if (errs && typeof errs === 'object') {
      Object.entries(errs).forEach(([key, msgs]) => {
        if (!BUDGET_VALIDATION_FIELDS.has(key)) return;
        const msg = Array.isArray(msgs) ? msgs.find(Boolean) : msgs;
        if (msg) fieldMsgs[key] = String(msg);
      });
    }
    setShowFieldErrors(true);
    setError(
      Object.keys(fieldMsgs).length
        ? formatFieldErrorsMessage(fieldMsgs)
        : (getFirstErrorMessage(data) || fallbackMessage),
    );
  };

  const doSave = async () => {
    try {
      setLoading(true);
      setError(null);
      const payload = buildPayload();
      if (isEditMode) {
        await axiosInstance.put(pendingUpdateRequest?.url ?? `/budgets/${id}`, payload);
      } else {
        await axiosInstance.post('/budgets', payload);
      }
      if (!isEditMode) formDraft.clearDraft();
      setSuccessMessage(`Budget item ${isEditMode ? 'updated' : 'created'} successfully!`);
      setTimeout(() => navigate('/budgets'), 1500);
    } catch (err) {
      handleApiError(err, `Failed to ${isEditMode ? 'update' : 'save'} budget item`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setConfirmDialogError('');
    if (isEditMode) {
      setError(null);
      setValidationErrors({});
      setShowFieldErrors(false);
      setPendingUpdateRequest({ url: `/budgets/${id}`, data: buildPayload() });
      setConfirmDialogOpen(true);
      return;
    }
    setError(null);
    if (!validateForm()) return;
    doSave();
  };

  const confirmUpdate = async () => {
    setConfirmDialogError('');
    const errors = collectFormErrors();
    if (Object.keys(errors).length) {
      setShowFieldErrors(true);
      rejectConfirmValidation({
        errors,
        setValidationErrors,
        setError,
        setConfirmUpdateDialogOpen: setConfirmDialogOpen,
        setPendingUpdateRequest,
      });
      return;
    }
    setConfirmDialogOpen(false);
    setPendingUpdateRequest(null);
    await doSave();
  };

  if (!user) return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <FeedbackMessage message="Please log in to access this page." severity="error"
        sx={{ borderRadius: '12px', fontFamily: "'DM Sans',sans-serif", background: alpha('#450a0a', 0.9), color: '#fca5a5', border: '1px solid rgba(248,113,113,0.3)' }} />
    </Container>
  );

  if (dataLoading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
      <Loading message="Loading budget form" size={56} variant="primary" />
    </Box>
  );

  if (isEditMode) {
    if (!budget?.id) return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <FeedbackMessage message={error || 'Budget item not found.'} severity="error"
          sx={{ borderRadius: '12px', fontFamily: "'DM Sans',sans-serif", background: alpha('#450a0a', 0.9), color: '#fca5a5', border: '1px solid rgba(248,113,113,0.3)' }} />
      </Container>
    );
    if (!canEditBudget(user, budget)) return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <FeedbackMessage message="You don't have permission to edit this budget item." severity="error"
          sx={{ borderRadius: '12px', fontFamily: "'DM Sans',sans-serif", background: alpha('#450a0a', 0.9), color: '#fca5a5', border: '1px solid rgba(248,113,113,0.3)' }} />
      </Container>
    );
  }

  const canSubmit = Boolean(
    formData.item.trim()
    && String(formData.amount ?? '').trim() !== ''
    && formData.year
    && formData.month,
  );
  const submitDisabled = loading || !canSubmit;

  const fieldHelper = (name) => (
    showFieldErrors && validationErrors[name] ? validationErrors[name] : undefined
  );
  const fieldHasError = (name) => showFieldErrors && !!validationErrors[name];

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      <Header
        title={isEditMode ? 'Edit Budget Item' : 'Create New Budget Item'}
        subtitle={isEditMode ? 'Update your budget item information' : 'Add a new item to your budget allocation'}
        icon={BudgetIcon} showBackButton onBackClick={() => navigate('/budgets')} backButtonText="Back to Budgets"
      />

      <FeedbackMessage message={successMessage} severity="success" onClose={() => setSuccessMessage('')}
        sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha(SUCCESS, 0.12), border: `1px solid ${alpha(SUCCESS, 0.25)}`, color: alpha(SUCCESS, 0.95) }} />
      <FeedbackMessage message={error} severity="error" onClose={() => setError(null)}
        sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5', boxShadow: '0 4px 20px rgba(239,68,68,0.12)' }} />

      {formDraft.showNotice && (
        <FormDraftNotice
          savedAt={formDraft.restoredAt}
          onDiscard={formDraft.discardDraft}
          onDismiss={formDraft.dismissNotice}
        />
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* ── Item Details ─────────────────────────────────────────────── */}
          <SectionCard title="Item Details" icon={<BudgetIcon />} delay={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

              {/* Item name */}
              <StyledField
                fullWidth required label="Item Name" name="item" value={formData.item}
                onChange={handleNativeChange} error={fieldHasError('item')} helperText={fieldHelper('item')}
                InputProps={{ startAdornment: adornment(NameIcon) }}
              />

              {/* Price | Unit */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5 }}>
                <StyledField
                  fullWidth label="Price (RM)" name="price" type="number" value={formData.price}
                  onChange={handleNativeChange} error={fieldHasError('price')} helperText={fieldHelper('price')}
                  InputProps={{ startAdornment: adornment(MoneyIcon) }}
                />
                <StyledField
                  fullWidth label="Unit / Quantity" name="unit" value={formData.unit}
                  onChange={handleNativeChange} error={fieldHasError('unit')} helperText={fieldHelper('unit')}
                  InputProps={{ startAdornment: adornment(UnitIcon) }}
                />
              </Box>

              {/* Inline calc hint — only visible when both price & unit are set */}
              <CalcHint price={formData.price} unit={formData.unit} amount={formData.amount} />

              {/* Amount (RM) */}
              <StyledField
                fullWidth required label="Amount (RM)" name="amount" type="number" value={formData.amount}
                onChange={handleNativeChange} error={fieldHasError('amount')} helperText={fieldHelper('amount')}
                InputProps={{ startAdornment: adornment(NumbersIcon) }}
              />

              {/* Divider before remark */}
              <Divider sx={{ borderColor: alpha(NEON, 0.07) }} />

              {/* Remark */}
              <StyledField
                fullWidth multiline minRows={2} label="Remark (optional)" name="remark" value={formData.remark}
                onChange={handleNativeChange} error={fieldHasError('remark')} helperText={fieldHelper('remark')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                      <NotesIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </SectionCard>

          {/* ── Budget Period ─────────────────────────────────────────────── */}
          <SectionCard title="Budget Period" icon={<DateRangeIcon />} delay={0.1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

              {/* Year | Month */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5 }}>
                <StyledSelect
                  required label="Year" value={formData.year}
                  onChange={(e) => handleChange('year', e.target.value)}
                  error={fieldHasError('year')} helperText={fieldHelper('year')}
                  startIcon={<CalendarIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                >
                  {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </StyledSelect>
                <StyledSelect
                  required label="Month" value={formData.month}
                  onChange={(e) => handleChange('month', e.target.value)}
                  error={fieldHasError('month')} helperText={fieldHelper('month')}
                  startIcon={<CalendarIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                >
                  {MONTHS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </StyledSelect>
              </Box>

            </Box>
          </SectionCard>

          <SubmitActionsCard
            statusText={isEditMode ? 'Save changes to update this budget item.' : 'Complete the form to add a new budget item.'}
            missingRequiredFields={isEditMode ? null : [!formData.item.trim() && 'Item Name', String(formData.amount ?? '').trim() === '' && 'Amount', !formData.year && 'Year', !formData.month && 'Month'].filter(Boolean)}
            readyStatusText="All required fields are complete. Ready to create budget item."
            cancelLabel="Cancel" onCancel={() => navigate('/budgets')} cancelDisabled={loading}
            submitLabel={isEditMode ? 'Update Budget' : 'Create Budget'}
            submitDisabled={submitDisabled} loading={loading} submitStartIcon={<SaveIcon />}
            showSaveDraft={formDraft.canSaveDraft}
            onSaveDraft={formDraft.saveDraft}
            draftSavedAt={formDraft.lastSavedAt}
          />
        </Box>
      </form>

      <ConfirmUpdateDialog
        open={confirmDialogOpen}
        onClose={() => { if (loading) return; setConfirmDialogOpen(false); setPendingUpdateRequest(null); setConfirmDialogError(''); }}
        onConfirm={confirmUpdate} title="Confirm Update" variant="edit" itemType="budget"
        itemName={formData.item} description="This action will update the budget item details and cannot be undone."
        confirmLabel={isEditMode ? 'Update Budget' : 'Add Budget'} loading={loading}
        confirmDisabled={!canSubmit}
        errorMessage={confirmDialogError}
      />
    </Container>
  );
};

export default BudgetForm;
